import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Text, Flex, SimpleGrid, Spinner, Input } from "@chakra-ui/react";
import { MdAdd, MdChevronRight, MdLocalOffer } from "react-icons/md";
import { Button } from "components/ui/Button";
import FormDialog from "components/utils/FormDialog";
import { useDialogForm } from "utils/useDialogForm";
import { useCreateDashboardTag, useDashboardTags } from "features/dashboard/tags/hooks";
import { sortTagsForDisplay } from "features/dashboard/tags/sort";

const Tags = () => {
  const navigate = useNavigate();
  const tagsQ = useDashboardTags();
  const createTag = useCreateDashboardTag();
  const addDialog = useDialogForm({ nombre: "" });

  const tags = useMemo(
    () => sortTagsForDisplay(Array.isArray(tagsQ.data) ? tagsQ.data : []),
    [tagsQ.data]
  );

  const onAdd = async () => {
    const nombre = addDialog.form.nombre.trim();
    if (!nombre) return;
    try {
      await createTag.mutateAsync(nombre);
      addDialog.close();
      addDialog.reset();
    } catch (_e) {}
  };

  return (
    <Box>
      <Flex
        direction={{ base: "column", md: "row" }}
        align={{ base: "stretch", md: "center" }}
        justify="space-between"
        gap="4"
        mb="6"
      >
        <Box>
          <Text
            fontFamily="display"
            fontWeight="700"
            fontSize={{ base: "2xl", md: "3xl" }}
            color="dashboard.fg.default"
            lineHeight="1.1"
          >
            Tags
          </Text>
          <Text fontSize="sm" color="dashboard.fg.muted" mt="0.5">
            Gestiona etiquetas asociadas a categorias
          </Text>
        </Box>

        <Button
          color="red"
          variant="solid"
          onClick={() => addDialog.openWith()}
          radius="xl"
          fontSize="lg"
          alignSelf={{ base: "stretch", md: "auto" }}
        >
          <MdAdd />
          Nuevo tag
        </Button>
      </Flex>

      {tagsQ.isLoading && (
        <Flex justify="center" align="center" py="12">
          <Spinner />
        </Flex>
      )}

      {tagsQ.isError && (
        <pre style={{ color: "crimson", whiteSpace: "pre-wrap" }}>
          {String(tagsQ.error?.message || tagsQ.error)}
        </pre>
      )}

      {!tagsQ.isLoading && tags.length === 0 && (
        <Box
          bg="dashboard.bg.surface"
          borderWidth="1px"
          borderColor="dashboard.border.muted"
          rounded="card"
          p="10"
          textAlign="center"
        >
          <Text color="dashboard.fg.muted">
            No hay tags. Crea uno con <strong>Nuevo tag</strong>.
          </Text>
        </Box>
      )}

      {!tagsQ.isLoading && tags.length > 0 && (
        <SimpleGrid columns={{ base: 1, sm: 2, md: 3, lg: 4 }} gap="4">
          {tags.map((tag) => (
            <Box
              key={tag.id}
              bg="dashboard.bg.surface"
              borderWidth="1px"
              borderColor="dashboard.border.muted"
              rounded="card"
              p="5"
              cursor="pointer"
              transition="transform 150ms ease, box-shadow 150ms ease"
              _hover={{ transform: "translateY(-2px)", boxShadow: "cardLift" }}
              onClick={() => navigate(`/dashboard/club/tags/${tag.id}`)}
            >
              <Flex align="center" gap="4">
                <Box
                  w="48px"
                  h="48px"
                  borderRadius="14px"
                  bg="teal.500"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  flexShrink={0}
                >
                  <MdLocalOffer size="24px" color="white" />
                </Box>
                <Box flex={1} minW={0}>
                  <Text
                    fontFamily="display"
                    fontWeight="700"
                    fontSize="md"
                    color="dashboard.fg.default"
                    lineClamp={1}
                  >
                    {tag.nombre}
                  </Text>
                </Box>
                <MdChevronRight
                  size="20px"
                  style={{ color: "var(--chakra-colors-dashboard-fg-subtle)", flexShrink: 0 }}
                />
              </Flex>
            </Box>
          ))}
        </SimpleGrid>
      )}

      <FormDialog
        open={addDialog.open}
        onOpenChange={addDialog.onOpenChange}
        title="Nuevo tag"
        saving={createTag.isPending}
        onCancel={addDialog.close}
        onSave={onAdd}
        saveLabel="Crear"
        errorText={createTag.isError ? String(createTag.error?.message || createTag.error) : ""}
      >
        <Input
          placeholder="Nombre del tag"
          bg="dashboard.bg.input"
          borderColor="dashboard.border.default"
          {...addDialog.bind("nombre")}
          onKeyDown={(e) => e.key === "Enter" && onAdd()}
        />
      </FormDialog>
    </Box>
  );
};

export default Tags;
